import * as React from 'react';
import { useParams, Link } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getGetHuntDetailQueryOptions,
  getGetHuntDetailQueryKey,
  useUpdateHunt,
  useSetHuntStatus,

  useCreateStation,
  useUpdateStation,
  useDeleteStation,
  useCreateChallenge,
  useUpdateChallenge,
  useDeleteChallenge,
  type StationDetail,
  type ChallengeDetail,
  type CreateStationRequest,
  type CreateChallengeRequest,
} from '@cdf/api-client-react';
import {
  ArrowLeftIcon, PlusIcon, PencilIcon, Trash2Icon, PrinterIcon,
  ExternalLinkIcon, CopyIcon, CheckIcon, QrCodeIcon, NavigationIcon,
  FileTextIcon, CameraIcon, MapPinIcon, HelpCircleIcon, SaveIcon,
  PlayIcon, PauseIcon, RotateCcwIcon, ScanLineIcon,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { MapPinPicker } from '@/components/map/map-pin-picker';
import { PageHeader } from '@/components/layout/page-header';
import {
  CELEBRATION_EFFECT_OPTIONS,
  useCvTargets,
  useUpdateCvTarget,
  type CelebrationEffect,
} from '@/lib/cv-api';

/* ── constants ────────────────────────────────────────────── */

const CHALLENGE_META: Record<string, { label: string; icon: typeof FileTextIcon }> = {
  demo_report: { label: 'Demo Report', icon: FileTextIcon },
  ar_view: { label: 'AR View', icon: CameraIcon },
  map_find: { label: 'Map Find', icon: MapPinIcon },
  quiz: { label: 'Quiz', icon: HelpCircleIcon },
  cv_scan: { label: 'CV Scan', icon: ScanLineIcon },
};

/* ── Station dialog ───────────────────────────────────────── */

function StationDialog({
  huntId,
  station,
  onClose,
  onSaved,
}: {
  huntId: string;
  station: StationDetail | null; // null = create mode
  onClose: () => void;
  onSaved: () => void;
}) {
  const queryClient = useQueryClient();
  const createMutation = useCreateStation();
  const updateMutation = useUpdateStation();
  const isEdit = !!station;

  const [name, setName] = React.useState('');
  const [kind, setKind] = React.useState<'qr' | 'gps'>('qr');
  const [points, setPoints] = React.useState('10');
  const [sortOrder, setSortOrder] = React.useState('0');
  const [latitude, setLatitude] = React.useState<number | undefined>();
  const [longitude, setLongitude] = React.useState<number | undefined>();
  const [captureRadiusM, setCaptureRadiusM] = React.useState('50');
  const [error, setError] = React.useState('');

  React.useEffect(() => {
    if (station) {
      setName(station.name);
      setKind(station.kind);
      setPoints(String(station.points));
      setSortOrder(String(station.sortOrder));
      setLatitude(station.latitude ?? undefined);
      setLongitude(station.longitude ?? undefined);
      setCaptureRadiusM(String(station.captureRadiusM ?? 50));
    } else {
      setName('');
      setKind('qr');
      setPoints('10');
      setSortOrder('0');
      setLatitude(undefined);
      setLongitude(undefined);
      setCaptureRadiusM('50');
    }
    setError('');
  }, [station]);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: getGetHuntDetailQueryKey(huntId) });
  };

  const handleSubmit = async () => {
    setError('');
    if (!name.trim()) {
      setError('Name is required');
      return;
    }
    if (kind === 'gps' && (latitude == null || longitude == null)) {
      setError('GPS stations require coordinates — use the map picker');
      return;
    }

    try {
      if (isEdit && station) {
        const data: Record<string, unknown> = {
          name: name.trim(),
          points: parseInt(points, 10) || 0,
          sortOrder: parseInt(sortOrder, 10) || 0,
        };
        if (kind === 'gps') {
          data.latitude = latitude;
          data.longitude = longitude;
          data.captureRadiusM = parseInt(captureRadiusM, 10) || 50;
        }
        await updateMutation.mutateAsync({ huntId, stationId: station.id, data });
      } else {
        const data: CreateStationRequest = {
          name: name.trim(),
          kind,
          points: parseInt(points, 10) || 0,
          sortOrder: parseInt(sortOrder, 10) || 0,
          ...(kind === 'gps'
            ? {
                latitude,
                longitude,
                captureRadiusM: parseInt(captureRadiusM, 10) || 50,
              }
            : {}),
        };
        await createMutation.mutateAsync({ huntId, data });
      }
      invalidate();
      onSaved();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save station');
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Station' : 'Add Station'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="st-name">Name</Label>
            <Input
              id="st-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Station 1"
              maxLength={100}
            />
          </div>

          {!isEdit && (
            <div className="space-y-2">
              <Label>Station Type</Label>
              <Select
                value={kind}
                onValueChange={(v) => setKind(v as 'qr' | 'gps')}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="qr">
                    <span className="flex items-center gap-2">
                      <QrCodeIcon className="h-4 w-4" /> QR — indoor (scan poster)
                    </span>
                  </SelectItem>
                  <SelectItem value="gps">
                    <span className="flex items-center gap-2">
                      <NavigationIcon className="h-4 w-4" /> GPS — outdoor (proximity)
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          {isEdit && (
            <div className="flex items-center gap-2">
              <Badge variant="outline">
                {kind === 'qr' ? (
                  <span className="flex items-center gap-1">
                    <QrCodeIcon className="h-3 w-3" /> QR
                  </span>
                ) : (
                  <span className="flex items-center gap-1">
                    <NavigationIcon className="h-3 w-3" /> GPS
                  </span>
                )}
              </Badge>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="st-points">Points</Label>
              <Input
                id="st-points"
                type="number"
                min={0}
                value={points}
                onChange={(e) => setPoints(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="st-order">Sort Order</Label>
              <Input
                id="st-order"
                type="number"
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
              />
            </div>
          </div>

          {kind === 'gps' && (
            <div className="space-y-3">
              <div className="space-y-2">
                <Label htmlFor="st-radius">Capture Radius (metres)</Label>
                <Input
                  id="st-radius"
                  type="number"
                  min={1}
                  value={captureRadiusM}
                  onChange={(e) => setCaptureRadiusM(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>Location</Label>
                <p className="text-xs text-muted-foreground">
                  Click the map to set the station position
                </p>
                <div className="overflow-hidden rounded-lg border">
                  <MapPinPicker
                    latitude={latitude}
                    longitude={longitude}
                    onLocationChange={(lat, lng) => {
                      setLatitude(lat);
                      setLongitude(lng);
                    }}
                  />
                </div>
              </div>
            </div>
          )}

          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={createMutation.isPending || updateMutation.isPending}
          >
            {createMutation.isPending || updateMutation.isPending
              ? 'Saving…'
              : isEdit
                ? 'Save Changes'
                : 'Add Station'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ── Challenge dialog ─────────────────────────────────────── */

function ChallengeDialog({
  huntId,
  stationId,
  challenge,
  onClose,
  onSaved,
}: {
  huntId: string;
  stationId: string;
  challenge: ChallengeDetail | null; // null = create mode
  onClose: () => void;
  onSaved: () => void;
}) {
  const queryClient = useQueryClient();
  const createMutation = useCreateChallenge();
  const updateMutation = useUpdateChallenge();
  const updateCvTargetMutation = useUpdateCvTarget();
  const isEdit = !!challenge;

  const [type, setType] = React.useState('demo_report');
  const [title, setTitle] = React.useState('');
  const [instructions, setInstructions] = React.useState('');
  const [points, setPoints] = React.useState('10');
  const [targetAssetId, setTargetAssetId] = React.useState('');
  const [question, setQuestion] = React.useState('');
  const [optionsText, setOptionsText] = React.useState('');
  const [correctIndex, setCorrectIndex] = React.useState('0');
  const [cvTargetId, setCvTargetId] = React.useState('');
  const [celebrationEffect, setCelebrationEffect] = React.useState<CelebrationEffect>('fireworks_streamers');
  const [error, setError] = React.useState('');

  const { data: cvTargetsData } = useCvTargets();
  const cvTargets = cvTargetsData?.targets ?? [];
  const selectedCvTarget = cvTargets.find((target) => target.id === cvTargetId);

  React.useEffect(() => {
    if (challenge) {
      setType(challenge.type);
      setTitle(challenge.title);
      setInstructions(challenge.instructions ?? '');
      setPoints(String(challenge.points));
      setTargetAssetId(challenge.config?.targetAssetId ?? '');
      setQuestion(challenge.config?.question ?? '');
      setOptionsText((challenge.config?.options ?? []).join('\n'));
      setCorrectIndex(String(challenge.config?.correctIndex ?? 0));
      const targetId = (challenge.config as { cvTargetId?: string } | null)?.cvTargetId ?? '';
      setCvTargetId(targetId);
    } else {
      setType('demo_report');
      setTitle('');
      setInstructions('');
      setPoints('10');
      setTargetAssetId('');
      setQuestion('');
      setOptionsText('');
      setCorrectIndex('0');
      setCvTargetId('');
      setCelebrationEffect('fireworks_streamers');
    }
    setError('');
  }, [challenge]);

  React.useEffect(() => {
    if (selectedCvTarget) {
      setCelebrationEffect(selectedCvTarget.celebrationEffect);
    }
  }, [selectedCvTarget?.id, selectedCvTarget?.celebrationEffect]);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: getGetHuntDetailQueryKey(huntId) });
  };

  const handleSubmit = async () => {
    setError('');
    if (!title.trim()) {
      setError('Title is required');
      return;
    }

    // Build config based on type
    let config: CreateChallengeRequest['config'] = null;
    let cvTargetForEffectUpdate: typeof selectedCvTarget;
    if (type === 'map_find') {
      if (!targetAssetId.trim()) {
        setError('Target asset ID is required for map_find challenges');
        return;
      }
      config = { targetAssetId: targetAssetId.trim() };
    } else if (type === 'quiz') {
      const opts = optionsText
        .split('\n')
        .map((s) => s.trim())
        .filter((s) => s.length > 0);
      if (!question.trim()) {
        setError('Question is required for quiz challenges');
        return;
      }
      if (opts.length < 2) {
        setError('Provide at least 2 answer options (one per line)');
        return;
      }
      config = {
        question: question.trim(),
        options: opts,
        correctIndex: parseInt(correctIndex, 10) || 0,
      };
    } else if (type === 'cv_scan') {
      if (!cvTargetId.trim()) {
        setError('Select a CV target for cv_scan challenges');
        return;
      }
      if (!selectedCvTarget) {
        setError('The selected CV target is still loading. Please try again.');
        return;
      }
      cvTargetForEffectUpdate = selectedCvTarget;
      config = { cvTargetId: cvTargetId.trim() } as unknown as typeof config;
    }

    try {
      if (isEdit && challenge) {
        await updateMutation.mutateAsync({
          huntId,
          stationId,
          challengeId: challenge.id,
          data: {
            title: title.trim(),
            instructions: instructions.trim() || undefined,
            points: parseInt(points, 10) || 0,
            config,
          },
        });
      } else {
        const data: CreateChallengeRequest = {
          type: type as CreateChallengeRequest['type'],
          title: title.trim(),
          instructions: instructions.trim() || undefined,
          points: parseInt(points, 10) || 0,
          config,
        } as CreateChallengeRequest;
        await createMutation.mutateAsync({ huntId, stationId, data });
      }
      if (cvTargetForEffectUpdate && cvTargetForEffectUpdate.celebrationEffect !== celebrationEffect) {
        await updateCvTargetMutation.mutateAsync({
          id: cvTargetForEffectUpdate.id,
          celebrationEffect,
        });
      }
      invalidate();
      onSaved();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save challenge');
    }
  };

  const parsedOptions = optionsText
    .split('\n')
    .map((s) => s.trim())
    .filter((s) => s.length > 0);

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Challenge' : 'Add Challenge'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {!isEdit && (
            <div className="space-y-2">
              <Label>Challenge Type</Label>
              <Select value={type} onValueChange={setType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(CHALLENGE_META).map(([value, meta]) => (
                    <SelectItem key={value} value={value}>
                      <span className="flex items-center gap-2">
                        <meta.icon className="h-4 w-4" /> {meta.label}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {isEdit && (
            <Badge variant="outline" className="flex w-fit items-center gap-1">
              {(() => {
                const M = CHALLENGE_META[type];
                return M ? (
                  <>
                    <M.icon className="h-3 w-3" /> {M.label}
                  </>
                ) : null;
              })()}
            </Badge>
          )}

          <div className="space-y-2">
            <Label htmlFor="ch-title">Title</Label>
            <Input
              id="ch-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Submit a demo report"
              maxLength={200}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="ch-instr">Instructions</Label>
            <Textarea
              id="ch-instr"
              value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
              placeholder="Walk participants through what they need to do…"
              rows={2}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="ch-points">Points</Label>
            <Input
              id="ch-points"
              type="number"
              min={0}
              value={points}
              onChange={(e) => setPoints(e.target.value)}
            />
          </div>

          {type === 'map_find' && (
            <div className="space-y-2">
              <Label htmlFor="ch-asset">Target Asset ID</Label>
              <Input
                id="ch-asset"
                value={targetAssetId}
                onChange={(e) => setTargetAssetId(e.target.value)}
                placeholder="Paste the asset UUID from the asset register"
                className="font-mono text-sm"
              />
              <p className="text-xs text-muted-foreground">
                Find the asset ID in the workspace asset register and paste it here
              </p>
            </div>
          )}

          {type === 'quiz' && (
            <>
              <div className="space-y-2">
                <Label htmlFor="ch-question">Question</Label>
                <Input
                  id="ch-question"
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                  placeholder="What type of asset is a stop sign?"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="ch-options">Answer Options (one per line)</Label>
                <Textarea
                  id="ch-options"
                  value={optionsText}
                  onChange={(e) => setOptionsText(e.target.value)}
                  placeholder={'Regulatory sign\nWarning sign\nGuide sign\nOther'}
                  rows={4}
                />
              </div>
              {parsedOptions.length >= 2 && (
                <div className="space-y-2">
                  <Label htmlFor="ch-correct">Correct Answer</Label>
                  <Select value={correctIndex} onValueChange={setCorrectIndex}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {parsedOptions.map((opt, i) => (
                        <SelectItem key={i} value={String(i)}>
                          {opt}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </>
          )}

          {type === 'cv_scan' && (
            <div className="space-y-2 rounded-md border border-primary/20 bg-primary/5 p-3">
              <Label htmlFor="ch-cv-target" className="flex items-center gap-1">
                <ScanLineIcon className="h-3 w-3" />
                CV Target
              </Label>
              <Select
                value={cvTargetId}
                onValueChange={(targetId) => {
                  setCvTargetId(targetId);
                  setCelebrationEffect(
                    cvTargets.find((target) => target.id === targetId)?.celebrationEffect ?? 'fireworks_streamers',
                  );
                }}
              >
                <SelectTrigger id="ch-cv-target">
                  <SelectValue placeholder="Select a trained CV target…" />
                </SelectTrigger>
                <SelectContent>
                  {cvTargets.map((t) => (
                    <SelectItem key={t.id} value={t.id}>
                      {t.name} ({Math.round(t.confidenceThreshold * 100)}%)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {cvTargetId && (
                <div className="space-y-2 border-t border-primary/20 pt-3">
                  <Label htmlFor="ch-cv-celebration">Celebration effect</Label>
                  <Select
                    value={celebrationEffect}
                    onValueChange={(value) => setCelebrationEffect(value as CelebrationEffect)}
                  >
                    <SelectTrigger id="ch-cv-celebration">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CELEBRATION_EFFECT_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    This updates the selected target and is used whenever it is detected in AR.
                  </p>
                </div>
              )}
              <p className="text-xs text-muted-foreground">
                Targets are trained in the{' '}
                <Link to="/workspace/cv-training" className="underline">
                  CV Training Studio
                </Link>
                . Participants scan the object in the AR view to auto-complete this challenge.
              </p>
              {cvTargets.length === 0 && (
                <p className="text-xs text-amber-600">
                  No CV targets registered yet. Create one in the CV Training Studio first.
                </p>
              )}
            </div>
          )}

          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={createMutation.isPending || updateMutation.isPending || updateCvTargetMutation.isPending}
          >
            {createMutation.isPending || updateMutation.isPending || updateCvTargetMutation.isPending
              ? 'Saving…'
              : isEdit
                ? 'Save Changes'
                : 'Add Challenge'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ── Main editor page ─────────────────────────────────────── */

export function HuntEditorPage() {
  const { huntId } = useParams<{ huntId: string }>();
  const queryClient = useQueryClient();

  const { data: hunt, isLoading, isError } = useQuery({
    ...getGetHuntDetailQueryOptions(huntId ?? ''),
    enabled: !!huntId,
  });

  const updateMutation = useUpdateHunt();
  const setStatusMutation = useSetHuntStatus();
  const deleteStationMutation = useDeleteStation();
  const deleteChallengeMutation = useDeleteChallenge();

  // Editable hunt fields
  const [editName, setEditName] = React.useState('');
  const [editDesc, setEditDesc] = React.useState('');
  const [editBonus, setEditBonus] = React.useState('0');
  const [editDirty, setEditDirty] = React.useState(false);

  React.useEffect(() => {
    if (hunt) {
      setEditName(hunt.name);
      setEditDesc(hunt.description ?? '');
      setEditBonus(String(hunt.completionBonusPoints));
      setEditDirty(false);
    }
  }, [hunt?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Dialog state
  const [stationDialog, setStationDialog] = React.useState<{
    station: StationDetail | null;
  } | null>(null);
  const [challengeDialog, setChallengeDialog] = React.useState<{
    stationId: string;
    challenge: ChallengeDetail | null;
  } | null>(null);

  // Copy state
  const [copiedCode, setCopiedCode] = React.useState<string | null>(null);

  const copyCode = async (code: string) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedCode(code);
      setTimeout(() => setCopiedCode(null), 2000);
    } catch {
      // clipboard not available
    }
  };

  const invalidate = () => {
    if (huntId) {
      queryClient.invalidateQueries({ queryKey: getGetHuntDetailQueryKey(huntId) });
    }
  };

  const handleSaveHunt = async () => {
    if (!huntId || !hunt) return;
    try {
      await updateMutation.mutateAsync({
        huntId,
        data: {
          name: editName.trim(),
          description: editDesc.trim() || undefined,
          completionBonusPoints: parseInt(editBonus, 10) || 0,
        },
      });
      invalidate();
      setEditDirty(false);
    } catch {
      // error shown by mutation state
    }
  };

  const handleStatusChange = async (status: 'draft' | 'active' | 'closed') => {
    if (!huntId) return;
    try {
      await setStatusMutation.mutateAsync({ huntId, data: { status } });
      invalidate();
    } catch {
      // ignore
    }
  };

  const handleDeleteStation = async (station: StationDetail) => {
    if (!huntId) return;
    if (!window.confirm(`Delete station "${station.name}" and all its challenges?`)) return;
    try {
      await deleteStationMutation.mutateAsync({ huntId, stationId: station.id });
      invalidate();
    } catch {
      // ignore
    }
  };

  const handleDeleteChallenge = async (
    stationId: string,
    challenge: ChallengeDetail
  ) => {
    if (!huntId) return;
    if (!window.confirm(`Delete challenge "${challenge.title}"?`)) return;
    try {
      await deleteChallengeMutation.mutateAsync({
        huntId,
        stationId,
        challengeId: challenge.id,
      });
      invalidate();
    } catch {
      // ignore
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-12">
        <p className="text-muted-foreground">Loading hunt…</p>
      </div>
    );
  }

  if (isError || !hunt) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="text-center space-y-3">
          <p className="text-muted-foreground">Hunt not found or you don&apos;t have access.</p>
          <Button asChild variant="outline" size="sm">
            <Link to="/workspace/hunts">Back to Hunts</Link>
          </Button>
        </div>
      </div>
    );
  }

  const sortedStations = [...hunt.stations].sort((a, b) => a.sortOrder - b.sortOrder);
  const stationsChallenges = (stationId: string) =>
    hunt.challenges.filter((c) => c.stationId === stationId);

  const statusBadge = (status: string) => {
    if (status === 'active')
      return <Badge className="bg-green-600 text-white hover:bg-green-600">Active</Badge>;
    if (status === 'closed') return <Badge variant="outline">Closed</Badge>;
    return <Badge variant="secondary">Draft</Badge>;
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title={hunt.name}
        description={`Slug: ${hunt.slug}`}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Button asChild variant="outline" size="sm">
              <Link to="/workspace/hunts">
                <ArrowLeftIcon className="mr-2 h-4 w-4" /> Hunts
              </Link>
            </Button>
            <Button asChild variant="outline" size="sm">
              <a href={`/hunt/${hunt.slug}`} target="_blank" rel="noopener noreferrer">
                <ExternalLinkIcon className="mr-2 h-4 w-4" /> Public
              </a>
            </Button>
            {hunt.status !== 'draft' && (
              <Button asChild variant="outline" size="sm">
                <a
                  href={`/hunt/${hunt.slug}/leaderboard/live`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <ExternalLinkIcon className="mr-2 h-4 w-4" /> Leaderboard
                </a>
              </Button>
            )}
            {hunt.stations.length > 0 && (
              <Button asChild variant="outline" size="sm">
                <Link to={`/workspace/hunts/${hunt.id}/posters`}>
                  <PrinterIcon className="mr-2 h-4 w-4" /> Posters
                </Link>
              </Button>
            )}
          </div>
        }
      />

      {/* Hunt details + status */}
      <div className="grid gap-6 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Hunt Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="he-name">Name</Label>
              <Input
                id="he-name"
                value={editName}
                onChange={(e) => {
                  setEditName(e.target.value);
                  setEditDirty(true);
                }}
                maxLength={100}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="he-desc">Description</Label>
              <Textarea
                id="he-desc"
                value={editDesc}
                onChange={(e) => {
                  setEditDesc(e.target.value);
                  setEditDirty(true);
                }}
                rows={2}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="he-bonus">Completion Bonus Points</Label>
              <Input
                id="he-bonus"
                type="number"
                min={0}
                value={editBonus}
                onChange={(e) => {
                  setEditBonus(e.target.value);
                  setEditDirty(true);
                }}
              />
            </div>
            {editDirty && (
              <Button
                size="sm"
                onClick={handleSaveHunt}
                disabled={updateMutation.isPending}
              >
                <SaveIcon className="mr-2 h-4 w-4" />
                {updateMutation.isPending ? 'Saving…' : 'Save Changes'}
              </Button>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Status</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center gap-2">
              {statusBadge(hunt.status)}
            </div>
            <p className="text-sm text-muted-foreground">
              {hunt.status === 'draft' &&
                'Hunt is not visible to participants yet. Activate when ready.'}
              {hunt.status === 'active' &&
                'Hunt is live! Participants can join and capture stations.'}
              {hunt.status === 'closed' &&
                'Hunt is closed. The leaderboard shows final results.'}
            </p>
            <div className="space-y-2">
              {hunt.status === 'draft' && (
                <Button
                  size="sm"
                  className="w-full"
                  onClick={() => handleStatusChange('active')}
                  disabled={setStatusMutation.isPending}
                >
                  <PlayIcon className="mr-2 h-4 w-4" /> Activate Hunt
                </Button>
              )}
              {hunt.status === 'active' && (
                <Button
                  size="sm"
                  variant="destructive"
                  className="w-full"
                  onClick={() => handleStatusChange('closed')}
                  disabled={setStatusMutation.isPending}
                >
                  <PauseIcon className="mr-2 h-4 w-4" /> Close Hunt
                </Button>
              )}
              {hunt.status === 'closed' && (
                <Button
                  size="sm"
                  variant="outline"
                  className="w-full"
                  onClick={() => handleStatusChange('active')}
                  disabled={setStatusMutation.isPending}
                >
                  <RotateCcwIcon className="mr-2 h-4 w-4" /> Reopen Hunt
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Stations */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">
            Stations ({sortedStations.length})
          </h2>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setStationDialog({ station: null })}
          >
            <PlusIcon className="mr-2 h-4 w-4" /> Add Station
          </Button>
        </div>

        {sortedStations.length === 0 ? (
          <Card>
            <CardContent className="py-8 text-center text-muted-foreground">
              No stations yet. Add QR stations for indoor check-ins and GPS stations
              for outdoor capture.
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {sortedStations.map((station) => {
              const challenges = stationsChallenges(station.id);
              return (
                <Card key={station.id}>
                  <CardContent className="p-4">
                    {/* Station header */}
                    <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
                      <div className="flex items-center gap-3">
                        {station.kind === 'qr' ? (
                          <QrCodeIcon className="h-5 w-5 text-sky-500" />
                        ) : (
                          <NavigationIcon className="h-5 w-5 text-violet-500" />
                        )}
                        <div>
                          <p className="font-semibold">{station.name}</p>
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Badge variant="outline" className="text-xs">
                              {station.kind.toUpperCase()}
                            </Badge>
                            <span>{station.points} pts</span>
                            <span>·</span>
                            <span>Order: {station.sortOrder}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setStationDialog({ station })}
                        >
                          <PencilIcon className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDeleteStation(station)}
                        >
                          <Trash2Icon className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </div>

                    {/* Station-specific info */}
                    {station.kind === 'qr' && (
                      <div className="mb-3 flex items-center gap-2 rounded-md bg-slate-50 px-3 py-2 dark:bg-slate-900">
                        <span className="text-xs text-muted-foreground">Secret code:</span>
                        <code className="font-mono text-xs">
                          {station.secretCode}
                        </code>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-6 px-2"
                          onClick={() => copyCode(station.secretCode)}
                        >
                          {copiedCode === station.secretCode ? (
                            <CheckIcon className="h-3 w-3 text-green-500" />
                          ) : (
                            <CopyIcon className="h-3 w-3" />
                          )}
                        </Button>
                      </div>
                    )}

                    {station.kind === 'gps' && (
                      <div className="mb-3 flex flex-wrap gap-3 text-xs text-muted-foreground">
                        <span>
                          Lat: {station.latitude?.toFixed(6)}
                        </span>
                        <span>
                          Lng: {station.longitude?.toFixed(6)}
                        </span>
                        <span>Radius: {station.captureRadiusM}m</span>
                      </div>
                    )}

                    {/* Challenges */}
                    <div className="mt-2">
                      <div className="mb-2 flex items-center justify-between">
                        <span className="text-sm font-medium text-muted-foreground">
                          Challenges ({challenges.length})
                        </span>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7"
                          onClick={() =>
                            setChallengeDialog({
                              stationId: station.id,
                              challenge: null,
                            })
                          }
                        >
                          <PlusIcon className="mr-1 h-3 w-3" /> Add
                        </Button>
                      </div>
                      {challenges.length === 0 ? (
                        <p className="text-xs text-muted-foreground">
                          No challenges attached to this station.
                        </p>
                      ) : (
                        <div className="space-y-1.5">
                          {challenges.map((ch) => {
                            const meta = CHALLENGE_META[ch.type];
                            const Icon = meta?.icon ?? HelpCircleIcon;
                            return (
                              <div
                                key={ch.id}
                                className="flex items-center justify-between rounded-md border px-3 py-2"
                              >
                                <div className="flex items-center gap-2">
                                  <Icon className="h-4 w-4 text-muted-foreground" />
                                  <span className="text-sm font-medium">{ch.title}</span>
                                  <Badge variant="secondary" className="text-xs">
                                    {ch.points} pts
                                  </Badge>
                                </div>
                                <div className="flex items-center gap-1">
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="h-7"
                                    onClick={() =>
                                      setChallengeDialog({
                                        stationId: station.id,
                                        challenge: ch,
                                      })
                                    }
                                  >
                                    <PencilIcon className="h-3 w-3" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="h-7"
                                    onClick={() =>
                                      handleDeleteChallenge(station.id, ch)
                                    }
                                  >
                                    <Trash2Icon className="h-3 w-3 text-destructive" />
                                  </Button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Dialogs */}
      {stationDialog && huntId && (
        <StationDialog
          key={stationDialog.station?.id ?? 'create'}
          huntId={huntId}
          station={stationDialog.station}
          onClose={() => setStationDialog(null)}
          onSaved={() => {}}
        />
      )}
      {challengeDialog && huntId && (
        <ChallengeDialog
          key={challengeDialog.challenge?.id ?? `create-${challengeDialog.stationId}`}
          huntId={huntId}
          stationId={challengeDialog.stationId}
          challenge={challengeDialog.challenge}
          onClose={() => setChallengeDialog(null)}
          onSaved={() => {}}
        />
      )}
    </div>
  );
}
